import type { TPatch, TPatchType } from '~/misc/patch/types';
import type {
  TErrorAttribute,
  TPaginated,
  TPaginationRequest,
  TSearchable,
  TSortingRequest,
} from '~/misc/resource/types';
import type { EFeatureName } from '~/misc/feature/types';
import type { ESaleType, TWrappedFilters } from '~/misc/listings/types';

enum ESubscriptionType {
  USER_SALE_OPPORTUNITY = 'user-sale-opportunity',
  USER_LET_OPPORTUNITY = 'user-let-opportunity',
}

enum EMarketingConsent {
  DENY = 0,
  GRANT = 1,
  NOT_ASKED = 2,
}

type TManager = {
  id: number;
  name: string;
  email: string;
};

type TManagerCompanyAlias = {
  id: number;
  name: string;
};

type TNewManagerCompany = {
  name: string;
  aliases: TManagerCompanyAlias[];
  sync_archived: boolean;
  sync_applicants: boolean;
  sync_landlords: boolean;
  excluded_marketing_consents: EMarketingConsent[];
  show_contact_info: boolean;
  allow_journal: boolean;
  write_journal: boolean;
};

type TManagerCompany = TNewManagerCompany & {
  id: number;
  users_count: number;
  created_at: string;
  patches_count: number;
  external_offices_count: number;
  opportunities_count: number;
  contacts_count: number;
  aliases_count: number;
  is_active: boolean;
  is_external: boolean;
  external_id: string | null;
};

type TManagerSearchCompany = {
  id: number;
  name: string;
};

type TPaginatedCompanyList = TPaginated<TManagerCompany>;

type TPaginatedCompanyPatches = TPaginated<TPatch>;

enum EManagerCompanyPatchesSorting {
  NAME = 'name',
  DISTRICTS = 'districts',
  DEPARTMENT = 'department',
  USERS = 'users',
}

type TManagerCompanyPatchesPayload = TPaginationRequest &
  TSearchable &
  TSortingRequest<EManagerCompanyPatchesSorting> & {
    type?: Exclude<TPatchType, 'both'>;
    patches?: number[];
  };

type TManagerCompanySearchPayload = {
  exclude: number[];
  q: string;
};

type TManagerCompanyUpdatePayload = TNewManagerCompany & {
  aliases: number[];
};

type TManagerCompanyBulkUsersSubscribePayload = {
  type: ESubscriptionType;
};

type TManagerCompanyBulkUsersUnsubscribePayload = TManagerCompanyBulkUsersSubscribePayload;

type TManagerCompanyBulkUsersStatsSubscriptions = {
  [key in ESubscriptionType]: number;
};

type TManagerCompanyBulkUsersStats = {
  total: number;
  active: number;
  subscriptions: TManagerCompanyBulkUsersStatsSubscriptions;
};

type TManagerFeature = {
  id: number;
  name: EFeatureName;
};

type TAutomatedEmailProvider = 'mailgun' | 'sendgrid';

type TAutomatedEmailProviderResponse = {
  provider: TAutomatedEmailProvider;
};

type TManagerCompanyErrors = {
  name?: string;
  sync_archived?: string;
  sync_applicants?: string;
  sync_landlords?: string;
  excluded_marketing_consents?: string;
  show_contact_info?: string;
  allow_journal?: string;
  write_journal?: string;
};

type TManagerCompanyCreatePayload = {
  name: string;
  owner_name: string;
  owner_email: string;
  external_id?: string;
  send_email: boolean;
  sync_archived: boolean;
};

type TManagerCompanyCreateErrors = {
  name?: string;
  owner_name?: string;
  owner_email?: string;
  external_id?: string;
  send_email?: string;
  sync_archived?: string;
};

type TManagerFeatureCreateErrors = {
  name?: TErrorAttribute;
};

enum EManagerCompanyListSorting {
  NAME = 'name',
  USERS = 'users',
  PATCHES = 'patches',
  OFFICES = 'offices',
  OPPORTUNITIES = 'opportunities',
  CONTACTS = 'contacts',
  CREATED_AT = 'created_at',
}

type TManagerCompanyListPayload = { external?: number } & TSearchable &
  TPaginationRequest &
  TSortingRequest<EManagerCompanyListSorting>;

type TManagerExternalOffice = {
  id: number;
  external_id: string;
  name: string;
  address: string | null;
  active: boolean;
  enabled: boolean;
  opportunities_count: number;
  contacts_count: number;
  users_count: number;
  sync_end_at: string | null;
  sync_start_at: string | null;
};

enum EManagerExternalOfficesSorting {
  NAME = 'name',
  ADDRESS = 'address',
  ACTIVE = 'active',
  ENABLED = 'enabled',
  OPPORTUNITIES = 'opportunities',
  CONTACTS = 'contacts',
  USERS = 'users',
}

type TManagerExternalOfficesIndexPayload = TPaginationRequest &
  TSearchable &
  TSortingRequest<EManagerExternalOfficesSorting>;

type TManagerPaginatedExternalOffices = TPaginated<TManagerExternalOffice>;

type TManagerAppointmentType = {
  id: number;
  name: string;
  is_market_appraisal: boolean;
};

type TManagerAppointmentTypeUpdatePayload = {
  is_market_appraisal: boolean;
};

type TManagerCompanyOpportunityLookupListing = {
  id: number;
  address: string | null;
  type: string | null;
  current_price: number | null;
  original_price: number | null;
  reduced_at: string | null;
  underoffer_at: string | null;
  sstc_at: string | null;
  listed_at: string | null;
  listing_url: string | null;
  status: string | null;
  agent_name: string | null;
};

type TManagerCompanyOpportunityLookupAppointment = {
  id: number;
  external_id: string | null;
  type: string | null;
  is_market_appraisal: boolean | null;
  description: string | null;
  external_created_at: string | null;
  external_updated_at: string | null;
};

type TManagerCompanyOpportunityLookupExternalEvent = {
  id: number;
  name: string;
  source: string | null;
  date: string;
  description: string | null;
};

type TManagerCompanyOpportunityLookup = {
  id: number;
  customer_reference: string;
  entity: string;
  source: string | null;
  record_created_date: string | null;
  last_record_updated_date: string | null;
  sales_valuation_date: string | null;
  lettings_valuation_date: string | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
  matched_input_address: string | null;
  property_id: number | null;
  property_address: string | null;
  listings: TManagerCompanyOpportunityLookupListing[];
  appointments: TManagerCompanyOpportunityLookupAppointment[];
  external_events: TManagerCompanyOpportunityLookupExternalEvent[];
};

type TManagerCompanyOpportunityLookupPayload = {
  customer_reference: string;
};

type TManagerCompanyFiltersGetPayload = {
  sale_type: ESaleType;
  patch_id: number;
};

type TManagerCompanyFiltersResponse = TWrappedFilters & {
  sale_type?: ESaleType;
};

type TManagerCompanyFiltersUpdatePayload = {
  patches: number[];
  sale_type: ESaleType;
  filters: TWrappedFilters;
};

export type {
  TManager,
  TNewManagerCompany,
  TManagerCompany,
  TManagerSearchCompany,
  TPaginatedCompanyList,
  TPaginatedCompanyPatches,
  TManagerCompanyPatchesPayload,
  TManagerCompanySearchPayload,
  TManagerCompanyUpdatePayload,
  TManagerCompanyBulkUsersSubscribePayload,
  TManagerCompanyBulkUsersUnsubscribePayload,
  TManagerCompanyBulkUsersStats,
  TManagerFeature,
  TAutomatedEmailProvider,
  TAutomatedEmailProviderResponse,
  TManagerCompanyErrors,
  TManagerCompanyCreatePayload,
  TManagerCompanyCreateErrors,
  TManagerFeatureCreateErrors,
  TManagerCompanyListPayload,
  TManagerExternalOffice,
  TManagerExternalOfficesIndexPayload,
  TManagerPaginatedExternalOffices,
  TManagerCompanyAlias,
  TManagerAppointmentType,
  TManagerAppointmentTypeUpdatePayload,
  TManagerCompanyOpportunityLookup,
  TManagerCompanyOpportunityLookupPayload,
  TManagerCompanyOpportunityLookupListing,
  TManagerCompanyOpportunityLookupAppointment,
  TManagerCompanyOpportunityLookupExternalEvent,
  TManagerCompanyFiltersGetPayload,
  TManagerCompanyFiltersResponse,
  TManagerCompanyFiltersUpdatePayload,
};

export {
  ESubscriptionType,
  EMarketingConsent,
  EManagerCompanyPatchesSorting,
  EManagerExternalOfficesSorting,
  EManagerCompanyListSorting,
};
